// PreferredAgeRange.js

import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  FormHelperText,
} from "@mui/material";

const PreferredAgeRange = ({ question, onChange }) => {
  // Destructure lower and upper range values and labels from question
  const { lowerRangeValue, upperRangeValue, lowerRangeLabel, upperRangeLabel } =
    question;

  // Initialize state for lower and upper range
  const [lowerValue, setLowerValue] = useState("");
  const [upperValue, setUpperValue] = useState("");
  const [error, setError] = useState("");

  // Effect to initialize state based on existing answer
  useEffect(() => {
    if (question.answer) {
      const [initialLower, initialUpper] = question.answer
        .split(",")
        .map(Number);
      setLowerValue(initialLower);
      setUpperValue(initialUpper);
    }
  }, [question.answer]);

  // Generate the range of options for the Select components
  const rangeValues = Array.from(
    { length: upperRangeValue - lowerRangeValue + 1 },
    (_, i) => lowerRangeValue + i
  );

  // Generate the formatted dropdown options
  const formattedOptions = rangeValues.map((rangeValue) => {
    if (rangeValue === lowerRangeValue && lowerRangeLabel) {
      return `${rangeValue} - ${lowerRangeLabel}`;
    }
    if (rangeValue === upperRangeValue && upperRangeLabel) {
      return `${rangeValue} - ${upperRangeLabel}`;
    }
    return rangeValue.toString();
  });

  // Handler for lower range change
  const handleLowerChange = (event) => {
    const newLower = event.target.value;
    setLowerValue(newLower);

    // Validate the new lower value against the current upper value
    let err;
    if (upperValue && newLower >= upperValue) {
      err = "Lower range must be less than upper range.";
    } else {
      err = "";
    }
    setError(err);

    // If valid, update the parent with the new answer
    if (!err) {
      const formattedAnswer = `${newLower},${upperValue}`;
      onChange(question._id, formattedAnswer);
    }
  };

  // Handler for upper range change
  const handleUpperChange = (event) => {
    const newUpper = event.target.value;
    setUpperValue(newUpper);

    // Validate the new upper value against the current lower value
    let err;
    if (lowerValue && newUpper <= lowerValue) {
      err = "Upper range must be greater than lower range.";
    } else {
      err = "";
    }
    setError(err);

    // If valid, update the parent with the new answer
    if (!err) {
      const formattedAnswer = `${lowerValue},${newUpper}`;
      onChange(question._id, formattedAnswer);
    }
  };

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom>
        {question.question}
        {question.required && " *"}
      </Typography>
      <Grid container spacing={2} alignItems="center">
        {/* Lower Range Select */}
        <Grid item xs={6}>
          <FormControl fullWidth error={Boolean(error)}>
            <InputLabel id="lower-range-label">Youngest</InputLabel>
            <Select
              labelId="lower-range-label"
              id="lower-range-select"
              value={lowerValue}
              label="Lower Range"
              onChange={handleLowerChange}
            >
              {rangeValues.map((rangeValue, index) => (
                <MenuItem key={rangeValue} value={rangeValue}>
                  {formattedOptions[index]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Upper Range Select */}
        <Grid item xs={6}>
          <FormControl fullWidth error={Boolean(error)}>
            <InputLabel id="upper-range-label">Oldest</InputLabel>
            <Select
              labelId="upper-range-label"
              id="upper-range-select"
              value={upperValue}
              label="Upper Range"
              onChange={handleUpperChange}
            >
              {rangeValues.map((rangeValue, index) => (
                <MenuItem key={rangeValue} value={rangeValue}>
                  {formattedOptions[index]}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>

      {/* Description */}
      {question.description && (
        <Typography variant="caption" color="textSecondary" mt={2}>
          {question.description}
        </Typography>
      )}
    </Grid>
  );
};

export default PreferredAgeRange;
